import React, {Fragment, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {AppLayoutMain, HeaderBar, NavigationBar, NotificationBar} from '@ace-de/ui-components/app-layout';
import {TabSet, Tab, TabBar, TabPanel} from '@ace-de/ui-components/navigation';
import {useStyles, ButtonGhost, LockBackdrop} from '@ace-de/ui-components';
import {Icon, caseProtocolIcon, infoIcon, closedIcon} from '@ace-de/ui-components/icons';
import {useTranslate} from '@computerrock/formation-i18n';
import {useSearchQueryParams} from '@computerrock/formation-router';
import {persistenceStates} from '@ace-de/eua-entity-types';
import ServiceAssignmentHeader from '../service-assignments/ui-elements/ServiceAssignmentHeader';
import satrScreenTabs from './satrScreenTabs';
import SATRServiceProviderTab from './SATRServiceProviderTab';
import SATRRequirementsTab from './SATRRequirementsTab';
import SATRCostCoverageTab from './SATRCostCoverageTab';
import createServiceCaseProtocolTabURL from '../service-cases/createServiceCaseProtocolTabURL';
import ScreenTabControls from '../service-cases/ui-elements/ScreenTabControls';
import * as serviceCaseSelectors from '../service-cases/serviceCaseSelectors';
import * as serviceAssignmentSelectors from '../service-assignments/serviceAssignmentSelectors';
import useServiceCaseLockedStatus from '../service-cases/useServiceCaseLockedStatus';


const ServiceAssignmentTrailerReturnScreen = props => {
    const {cx} = useStyles();
    const {serviceCase, serviceAssignment} = props;
    const {translate, createTranslateShorthand} = useTranslate();
    const translateScreen = createTranslateShorthand('service_assignment_trailer_return_screen');
    const {activeTab} = useSearchQueryParams();
    const {isServiceCaseLocked, lockedBy} = useServiceCaseLockedStatus(serviceCase);
    const topElementRef = useRef();

    useEffect(() => {
        if (!topElementRef.current) return;
        topElementRef.current.scrollIntoView();
    }, [topElementRef]);

    if (!serviceAssignment || !serviceCase) return null;

    return (
        <Fragment>
            <div ref={topElementRef} />
            <HeaderBar>
                <ServiceAssignmentHeader serviceCase={serviceCase} serviceAssignment={serviceAssignment} />
            </HeaderBar>
            <TabSet
                name="satr-screen-tab-set"
                isRoutingEnabled={true}
                qaIdent="satr-screen"
            >
                <NavigationBar>
                    <div
                        className={cx([
                            'global!ace-u-flex',
                            'global!ace-u-flex--direction-row',
                            'global!ace-u-flex--align-center',
                        ])}
                    >
                        <TabBar
                            name="satr-screen-tabs"
                            tabSet="satr-screen-tab-set"
                            defaultValue={activeTab || satrScreenTabs.REQUIREMENTS}
                            isDisabled={serviceAssignment.persistenceState === persistenceStates.PENDING}
                            className={cx('global!ace-u-flex--grow-1')}
                        >
                            <Tab name={satrScreenTabs.REQUIREMENTS} value={satrScreenTabs.REQUIREMENTS}>
                                {translateScreen('tab_label.requirements')}
                            </Tab>
                            <Tab name={satrScreenTabs.SERVICE_PROVIDER} value={satrScreenTabs.SERVICE_PROVIDER}>
                                {translateScreen('tab_label.service_provider')}
                            </Tab>
                            <Tab name={satrScreenTabs.COST_COVERAGE} value={satrScreenTabs.COST_COVERAGE}>
                                {translateScreen('tab_label.cost_coverage')}
                            </Tab>
                        </TabBar>
                        <ButtonGhost
                            className={cx([
                                'global!ace-u-flex--align-self-stretch',
                                'global!ace-u-margin--right-32',
                            ])}
                            onClick={() => window.open(createServiceCaseProtocolTabURL(serviceCase.id), '_blank')}
                        >
                            <Icon
                                icon={caseProtocolIcon}
                                className={cx('global!ace-u-margin--right-8')}
                            />
                            {translateScreen('button_label.case_protocol')}
                        </ButtonGhost>
                    </div>
                </NavigationBar>
                <div>
                    {isServiceCaseLocked && (
                        <NotificationBar type="information">
                            <div
                                className={cx([
                                    'global!ace-u-width--full',
                                    'global!ace-u-margin--16-0',
                                    'global!ace-u-flex',
                                    'global!ace-u-flex--justify-center',
                                    'global!ace-u-typography--variant-body-medium',
                                ])}
                            >
                                <Icon
                                    icon={closedIcon}
                                    className={cx('global!ace-u-margin--right-8')}
                                />
                                {translate('global.case_lock_check.case_lock_notification', {
                                    userName: lockedBy,
                                })}
                            </div>
                        </NotificationBar>
                    )}
                    {!serviceAssignment?.entitlementCheck && (
                    <NotificationBar type="negative">
                        <div
                            className={cx([
                                'global!ace-u-width--full',
                                'global!ace-u-margin--16-0',
                                'global!ace-u-flex',
                                'global!ace-u-flex--justify-center',
                                'global!ace-u-flex--align-center',
                                'global!ace-u-typography--variant-body-medium',
                                'global!ace-u-typography--color-contrast',
                            ])}
                        >
                            <Icon
                                icon={infoIcon}
                                className={cx([
                                    'ace-c-icon--color-contrast',
                                    'global!ace-u-margin--right-8',
                                ])}
                            />
                            <div>
                                {Array.isArray(serviceAssignment.entitlementResponses)
                                    && serviceAssignment.entitlementResponses.map((response, idx) => {
                                        return (
                                            <Fragment key={`${response.entitlementError}-${idx}`}>
                                                <span>{translate(response.message)}</span>
                                                {idx < serviceAssignment.entitlementResponses.length - 1 && (
                                                    <br />
                                                )}
                                            </Fragment>
                                        );
                                    })
                                }
                            </div>
                        </div>
                    </NotificationBar>
                    )}
                </div>
                <AppLayoutMain
                    contentClassName={cx([
                        'global!ace-u-flex',
                        'global!ace-u-flex--direction-column',
                        'global!ace-u-flex--justify-space-between',
                        'global!ace-u-flex--align-stretch',
                    ])}
                >
                    {isServiceCaseLocked && (
                    <LockBackdrop />
                    )}
                    <TabPanel for={satrScreenTabs.REQUIREMENTS}>
                        <SATRRequirementsTab />
                    </TabPanel>
                    <TabPanel for={satrScreenTabs.SERVICE_PROVIDER}>
                        <SATRServiceProviderTab />
                    </TabPanel>
                    <TabPanel for={satrScreenTabs.COST_COVERAGE}>
                        <SATRCostCoverageTab />
                    </TabPanel>
                    <ScreenTabControls activeTab={activeTab} screenTabs={satrScreenTabs} />
                </AppLayoutMain>
            </TabSet>
        </Fragment>
    );
};

ServiceAssignmentTrailerReturnScreen.propTypes = {
    serviceCase: PropTypes.object,
    serviceAssignment: PropTypes.object,
};

ServiceAssignmentTrailerReturnScreen.defaultProps = {
    serviceCase: null,
    serviceAssignment: null,
};

const mapStateToProps = (state, props) => {
    const serviceCaseSelector = serviceCaseSelectors.createServiceCaseSelector();
    const serviceAssignmentSelector = serviceAssignmentSelectors.createServiceAssignmentSelector();
    return {
        serviceCase: serviceCaseSelector(state, props),
        serviceAssignment: serviceAssignmentSelector(state, props),
    };
};
export default connect(mapStateToProps, null)(ServiceAssignmentTrailerReturnScreen);
